import { useEffect, useState, type ChangeEvent } from 'react';
import {
  Fingerprint,
  ShieldCheck,
  Upload,
  Database,
  Trash2,
  CheckCircle2,
  XCircle,
  Loader2,
  FlaskConical,
  Lock,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import {
  listEdmDatasets,
  createEdmDataset,
  deleteEdmDataset,
  getEdmDataset,
  matchEdmValue,
  type EdmDatasetSummary,
  type EdmMatchResult,
} from '@/lib/api';

// -- client-side crypto (plaintext never leaves the browser) --------------

function toHex(buf: ArrayBuffer): string {
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Lowercase hex SHA-256 of the input string. Matches the backend contract:
 *  the stored digest is SHA-256(salt + value). */
async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return toHex(digest);
}

/** 16 random bytes = 32 lowercase hex chars. */
function randomSaltHex(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function parseValues(raw: string): string[] {
  return raw
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
}

const MAX_HASHES = 200000;

export function DiscoveryEdm() {
  const [datasets, setDatasets] = useState<EdmDatasetSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // New-dataset form
  const [name, setName] = useState('');
  const [fieldLabel, setFieldLabel] = useState('PAN');
  const [rawValues, setRawValues] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createResult, setCreateResult] = useState<{ rows: number; parsed: number } | null>(null);

  // Test-a-value box
  const [testDatasetId, setTestDatasetId] = useState('');
  const [testInput, setTestInput] = useState('');
  const [testing, setTesting] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<EdmMatchResult | null>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await listEdmDatasets();
      setDatasets(rows);
      if (rows.length > 0 && !testDatasetId) setTestDatasetId(rows[0].id);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'failed to load datasets');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parsedCount = parseValues(rawValues).length;

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const isCsv = file.name.toLowerCase().endsWith('.csv');
    const vals = text
      .split(/\r?\n/)
      .map((line) => (isCsv ? (line.split(',')[0] ?? '') : line).trim())
      .filter(Boolean);
    setRawValues(vals.join('\n'));
    // Reset the input so re-selecting the same file re-fires onChange.
    e.target.value = '';
  }

  async function createDataset() {
    const values = parseValues(rawValues);
    if (!name.trim() || !fieldLabel.trim() || values.length === 0) return;
    if (values.length > MAX_HASHES) {
      setCreateError(`too many values (${values.length}); the limit is ${MAX_HASHES}`);
      return;
    }
    setCreating(true);
    setCreateError(null);
    setCreateResult(null);
    try {
      // Salt + hash entirely in the browser. Only the salt + hashes upload.
      const salt = randomSaltHex();
      const hashes = await Promise.all(values.map((v) => sha256Hex(salt + v)));
      const res = await createEdmDataset({
        name: name.trim(),
        fieldLabel: fieldLabel.trim(),
        salt,
        hashes,
      });
      setCreateResult({ rows: res.rowCount, parsed: values.length });
      setName('');
      setRawValues('');
      await refresh();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'failed to create dataset');
    } finally {
      setCreating(false);
    }
  }

  async function removeDataset(id: string) {
    setDeletingId(id);
    try {
      await deleteEdmDataset(id);
      if (testDatasetId === id) setTestDatasetId('');
      await refresh();
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'failed to delete dataset');
    } finally {
      setDeletingId(null);
    }
  }

  async function testValue() {
    if (!testDatasetId || !testInput.trim()) return;
    setTesting(true);
    setTestError(null);
    setTestResult(null);
    try {
      // Fetch the dataset salt, hash the test value client-side, then match.
      const detail = await getEdmDataset(testDatasetId);
      const valueHash = await sha256Hex(detail.salt + testInput.trim());
      const res = await matchEdmValue(testDatasetId, valueHash);
      setTestResult(res);
    } catch (e) {
      setTestError(e instanceof Error ? e.message : 'match failed');
    } finally {
      setTesting(false);
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Exact-match (EDM)"
        subtitle="Upload your own list of sensitive values (high-value PANs, card numbers, account IDs). The browser salts and hashes each value, so plaintext never reaches the platform. Scans then flag exact matches against your list."
        badges={[
          { label: 'Salted SHA-256', variant: 'glass' },
          { label: 'Zero-plaintext', variant: 'info' },
        ]}
      />

      <div className="mb-4 flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-200/90">
        <ShieldCheck className="h-4 w-4 shrink-0 mt-0.5" />
        <span>
          Plaintext never leaves your browser. Each value is combined with a random per-dataset
          salt and SHA-256 hashed locally; only the salt and the resulting hashes are uploaded.
          The platform stores hashes and can confirm a match without ever seeing the underlying
          value.
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* New dataset */}
        <GlassCard className="p-6">
          <CardHeader className="p-0 mb-4">
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-4 w-4" /> New dataset
            </CardTitle>
            <CardDescription>
              Paste one value per line, or load a .csv / .txt file. For CSV the first column of
              each row is used.
            </CardDescription>
          </CardHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Dataset name
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. High-value merchant PANs"
                  maxLength={120}
                  className="mt-1 block w-full glass-lite rounded px-2 py-1.5 text-xs bg-transparent border border-border/40 focus:outline-none focus:border-primary"
                />
              </label>
              <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Field label
                <input
                  value={fieldLabel}
                  onChange={(e) => setFieldLabel(e.target.value)}
                  placeholder="e.g. PAN"
                  maxLength={80}
                  className="mt-1 block w-full glass-lite rounded px-2 py-1.5 text-xs bg-transparent border border-border/40 focus:outline-none focus:border-primary"
                />
              </label>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  Values (one per line)
                </label>
                <label className="text-[10px] text-primary cursor-pointer inline-flex items-center gap-1 hover:underline">
                  <Upload className="h-3 w-3" /> Load file
                  <input
                    type="file"
                    accept=".csv,.txt,text/csv,text/plain"
                    className="hidden"
                    onChange={(e) => void onFile(e)}
                  />
                </label>
              </div>
              <textarea
                value={rawValues}
                onChange={(e) => setRawValues(e.target.value)}
                rows={8}
                placeholder={'ABCPD1234K\nXYZAB9876Q\n...'}
                className="block w-full glass-lite rounded px-2 py-1.5 text-xs font-mono bg-transparent border border-border/40 focus:outline-none focus:border-primary resize-y"
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                {parsedCount} value{parsedCount === 1 ? '' : 's'} parsed. Limit {MAX_HASHES.toLocaleString()}.
              </p>
            </div>

            {createError && (
              <div className="text-xs text-destructive glass-lite border border-destructive/40 rounded-md p-2">
                {createError}
              </div>
            )}
            {createResult && (
              <div className="text-xs glass-lite border border-success/40 rounded-md p-2 text-emerald-200/90">
                <CheckCircle2 className="h-3.5 w-3.5 inline text-success" /> Uploaded{' '}
                <strong>{createResult.rows.toLocaleString()}</strong> salted hashes
                {createResult.parsed !== createResult.rows && (
                  <> (deduped from {createResult.parsed.toLocaleString()})</>
                )}
                . Plaintext never left your browser.
              </div>
            )}

            <Button
              onClick={() => void createDataset()}
              disabled={creating || !name.trim() || !fieldLabel.trim() || parsedCount === 0}
              className="w-full"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              {creating ? 'Salting and hashing in-browser...' : 'Salt, hash, and upload'}
            </Button>
          </div>
        </GlassCard>

        {/* Test a value */}
        <GlassCard className="p-6">
          <CardHeader className="p-0 mb-4">
            <CardTitle className="flex items-center gap-2">
              <FlaskConical className="h-4 w-4" /> Test a value
            </CardTitle>
            <CardDescription>
              Check whether a value is an exact match against a dataset. The value is hashed
              client-side with the dataset salt; only the hash is sent.
            </CardDescription>
          </CardHeader>
          <div className="space-y-3">
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Dataset
              <select
                value={testDatasetId}
                onChange={(e) => {
                  setTestDatasetId(e.target.value);
                  setTestResult(null);
                }}
                className="mt-1 block w-full glass-lite rounded px-2 py-1.5 text-xs bg-transparent border border-border/40"
              >
                <option value="">Select a dataset...</option>
                {datasets.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({d.rowCount.toLocaleString()} {d.fieldLabel})
                  </option>
                ))}
              </select>
            </label>

            <label className="text-[10px] uppercase tracking-wider text-muted-foreground">
              Value to test
              <input
                value={testInput}
                onChange={(e) => setTestInput(e.target.value)}
                placeholder="e.g. ABCPD1234K"
                className="mt-1 block w-full glass-lite rounded px-2 py-1.5 text-xs font-mono bg-transparent border border-border/40 focus:outline-none focus:border-primary"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void testValue();
                }}
              />
            </label>

            <Button
              variant="glass"
              onClick={() => void testValue()}
              disabled={testing || !testDatasetId || !testInput.trim()}
              className="w-full"
            >
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Fingerprint className="h-4 w-4" />}
              {testing ? 'Hashing and matching...' : 'Test for exact match'}
            </Button>

            {testError && (
              <div className="text-xs text-destructive glass-lite border border-destructive/40 rounded-md p-2">
                {testError}
              </div>
            )}

            {testResult &&
              (testResult.matched ? (
                <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 p-4 text-center">
                  <CheckCircle2 className="h-8 w-8 mx-auto text-success mb-2" />
                  <div className="text-base font-semibold text-emerald-200">
                    EXACT MATCH in {testResult.datasetName}
                  </div>
                  <div className="text-[11px] text-emerald-200/70 mt-1">
                    This value is present in your uploaded list.
                  </div>
                </div>
              ) : (
                <div className="rounded-md border border-border/40 bg-muted/30 p-4 text-center">
                  <XCircle className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <div className="text-base font-semibold text-muted-foreground">No match</div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    This value is not in {testResult.datasetName}.
                  </div>
                </div>
              ))}
          </div>
        </GlassCard>
      </div>

      {/* Dataset list */}
      <GlassCard className="p-6 mt-4">
        <CardHeader className="p-0 mb-4">
          <CardTitle className="flex items-center gap-2">
            <Database className="h-4 w-4" /> Uploaded datasets
          </CardTitle>
          <CardDescription>Each dataset holds salted hashes only, never plaintext.</CardDescription>
        </CardHeader>

        {loadError && (
          <div className="text-xs text-destructive glass-lite border border-destructive/40 rounded-md p-2 mb-3">
            {loadError}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : datasets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
            <Fingerprint className="h-14 w-14 opacity-20 mb-3" />
            <p className="text-sm">No datasets yet. Upload a sensitive column to get started.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {datasets.map((d) => (
              <div
                key={d.id}
                className="grid grid-cols-12 items-center gap-3 p-3 rounded-md glass-lite hover:bg-muted/40 transition-colors"
              >
                <div className="col-span-6 min-w-0">
                  <div className="text-sm font-medium truncate">{d.name}</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    Created {new Date(d.createdAt).toLocaleString()}
                  </div>
                </div>
                <div className="col-span-2 flex justify-center">
                  <Badge variant="glass" className="text-[9px]">
                    {d.fieldLabel}
                  </Badge>
                </div>
                <div className="col-span-2 text-right">
                  <div className="text-sm font-semibold tabular">{d.rowCount.toLocaleString()}</div>
                  <div className="text-[9px] text-muted-foreground uppercase tracking-wider">hashes</div>
                </div>
                <div className="col-span-2 flex justify-end">
                  <Button
                    variant="glass"
                    size="sm"
                    disabled={deletingId === d.id}
                    onClick={() => void removeDataset(d.id)}
                  >
                    {deletingId === d.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="h-3.5 w-3.5" />
                    )}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </GlassCard>
    </PageContainer>
  );
}
